import clsx from 'clsx';
import React from 'react';

import styles from './Divider.module.css';

/** Visual orientation of the separator line. */
export type DividerOrientation = 'horizontal' | 'vertical';

/**
 * Named spacing scale mapped to the `--divider-*` tokens (see `tokens.css`):
 * `xs` 4px · `sm` 8px · `md` 12px · `lg` 16px · `xl` 24px · `xxl` 48px (desktop values).
 */
export type DividerSpacing = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

/**
 * Semantic color mapped to a design-system border token (see `tokens.css`).
 * Each token is theme-aware and automatically swaps in dark mode.
 */
export type DividerColor = 'default' | 'subtle' | 'strong' | 'success' | 'warning' | 'error' | 'info' | 'focus';

export interface DividerProps extends React.HTMLAttributes<HTMLHRElement> {
    /**
     * Visual orientation of the separator.
     * @default 'horizontal'
     */
    orientation?: DividerOrientation;

    /**
     * Margin applied on the axis perpendicular to the line, using the named
     * `--divider-*` tokens. `spacing="xl"` → `var(--divider-xl)` (24px) margin
     * above/below a horizontal line (or left/right of a vertical line).
     *
     * Omit the prop when the parent already manages spacing.
     */
    spacing?: DividerSpacing;

    /**
     * Semantic border color drawn from the design-system token palette.
     * Automatically adjusts in dark mode.
     * @default 'default'
     */
    color?: DividerColor;

    /** Additional class names to merge onto the root element. */
    className?: string;
}

/**
 * `Divider` renders a thin separator line (`<hr>`) with configurable orientation,
 * spacing, and semantic color. Styling is driven by `data-*` attributes so
 * consumer CSS can target them without knowledge of internal class names.
 *
 * @example
 * // Basic horizontal divider
 * <Divider />
 *
 * @example
 * // Vertical divider with extra-large spacing and error color
 * <Divider orientation="vertical" spacing="xl" color="error" />
 */
const Divider = React.forwardRef<HTMLHRElement, DividerProps>(
    ({ orientation = 'horizontal', spacing, color = 'default', className, ...rest }, ref) => (
        <hr
            ref={ref}
            aria-orientation={orientation}
            data-orientation={orientation}
            data-spacing={spacing}
            data-color={color}
            className={clsx(styles.divider, className)}
            {...rest}
        />
    )
);

Divider.displayName = 'Divider';

export { Divider };
export default Divider;
